import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { authClient } from './lib/auth-client';
import { usePostHogIdentify, usePostHogPageviews } from './hooks/usePostHog';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import SettingsPage from './pages/SettingsPage';
import VendorsPage from './pages/VendorsPage';
import VendorDetailPage from './pages/VendorDetailPage';
import CompareQuotesPage from './pages/CompareQuotesPage';
import QuoteEditorPage from './pages/QuoteEditorPage';
import { Toaster } from '@/components/ui/sonner';
import { BatchUploadProvider } from './contexts/BatchUploadContext';
import { BatchUploadPanel } from './components/BatchUploadPanel';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

function ProtectedRoute({ children, fullWidth }: { children: React.ReactNode; fullWidth?: boolean }) {
  const { data: session, isPending } = authClient.useSession();

  if (isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="display-tight text-lg text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  return <Layout fullWidth={fullWidth}>{children}</Layout>;
}

function PostHogTracker() {
  usePostHogIdentify();
  usePostHogPageviews();
  return null;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BatchUploadProvider>
        <BrowserRouter>
          <PostHogTracker />
          <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/" element={<Navigate to="/projects" replace />} />
          <Route path="/dashboard" element={<Navigate to="/projects" replace />} />
          <Route
            path="/projects"
            element={
              <ProtectedRoute>
                <ProjectsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:id"
            element={
              <ProtectedRoute>
                <ProjectDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:id/items/:itemId/compare"
            element={
              <ProtectedRoute>
                <CompareQuotesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:id/items/:itemId/quote-editor"
            element={
              <ProtectedRoute fullWidth>
                <QuoteEditorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendors"
            element={
              <ProtectedRoute>
                <VendorsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendors/:id"
            element={
              <ProtectedRoute>
                <VendorDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          </Routes>
          <BatchUploadPanel />
        </BrowserRouter>
        <Toaster />
      </BatchUploadProvider>
    </QueryClientProvider>
  );
}
